<!--
  ProductTileGroup.vue

  Shared Vue mount used to replace every Liquid `product-grid-item` render across
  HP / PDP / brick / mega-menu surfaces. The mount fetches product data via
  `FastSimonSDK.getProducts` (by ID) and renders one `ProductListItem` per ID,
  preserving the order of `data-pids`.

  Mount target:
    <div class="product-tile-group" id="ptg-{unique}" data-pids='[...ids]' ...></div>

  Recognized data attributes on the mount target:
    data-pids                          (required) JSON array of Shopify product IDs in render order
    data-product-swatch-name           Optional swatch metafield name for ProductListItem
    data-show-crosssell-percentage     'true' to show sale percentage in tiles
    data-hide-swatches                 Defaults to 'true' (swatches hidden); set 'false' to show
    data-hide-quickview                'true' to hide quickview action
    data-disable-slider                'true' to disable inner image carousel
    data-hide-skeleton                 Per-mount override. Skeleton is also
                                       globally controlled by the
                                       "Enable loading shimmer" admin checkbox
                                       (settings.loading_shimmer_enable);
                                       skeleton renders only when BOTH the
                                       global setting is on AND this attr
                                       is not 'true'.
    data-grid-large / data-grid-small  Hint sizes forwarded to ProductListItem
    data-item-wrap-class               Optional CSS class for per-tile wrapper div
    data-as-carousel-slides            'true' to also emit data-carousel-slide on each
                                       wrapper (default wrap class becomes
                                       'product-grid-slide'); used by Flickity surfaces
    data-after-mount-event             Custom event name to dispatch (bubbles) on the
                                       parent after products render; e.g.
                                       'theme:recent-products:added' to retrigger
                                       Flickity init on recently-viewed
    data-collection-id / data-collection-handle  Optional context for FS event tracking
    data-fs-widget-id                  Optional FS widget id for impression tracking

  FS flags used:
    direct: true, withAttributes: true, withProductVariants: true, withSiblings: true.
    withBadges is intentionally NOT requested — merch badge logic remains driven by
    admin/metafield data inside ProductListItem.

  Auto-mount is wired in `src/main.js` (`bootstrapProductTileGroups`) for all
  `.product-tile-group` roots present on initial load (and on
  `shopify:section:load` in design mode). Programmatic mounts (e.g. recently
  viewed, which sets data-pids at runtime after cookie resolution) should call
  `window.mountProductTileGroup(rootEl)`.
-->
<script>
  import ProductListItem from '@/components/ProductListItem.vue';
  import { whenFastSimonReady, getProductMetafieldFromFS, getProductCollectionMetafieldFromFS, logFastSimonError, handleViewportSize } from '@/lib/vue-helpers.js';
  import { emitter } from '@/main.js';

  export default {
    name: 'ProductTileGroup',
    components: {
      ProductListItem
    },
    data() {
      return {
        pids: [],
        productsOnDisplay: [],
        productImageName: '',
        showCrosssellPercentage: 'false',
        hideSwatches: false,
        hideQuickview: false,
        disableSlider: false,
        hideSkeleton: false,
        showSalePercentage: false,
        gridLarge: 0,
        gridSmall: 0,
        wrapClass: '',
        asCarouselSlides: false,
        afterMountEvent: '',
        collectionId: '',
        collectionHandle: '',
        fsWidgetId: '',
        productCategoryMetafieldOneAttr: '',
        productCategoryMetafieldTwoAttr: '',
        productCategoryMetafieldOneCollectionAttr: '',
        productCategoryMetafieldTwoCollectionAttr: '',
        productReminderMetafieldEmbedAttr: '',
        setReminderLinkText: '',
        hasLoaded: false,
        loadFailed: false
      };
    },
    computed: {
      shouldRender() {
        return this.hasLoaded && this.productsOnDisplay.length > 0;
      }
    },
    mounted() {
      const parent = this.$el.parentElement;
      if (!parent) return;

      this.handleViewportSize = handleViewportSize(this);

      const rawPids = parent.getAttribute('data-pids') || '[]';
      try {
        const parsed = JSON.parse(rawPids);
        this.pids = Array.isArray(parsed) ? parsed.map(id => String(id)).filter(Boolean) : [];
      } catch (e) {
        logFastSimonError('ProductTileGroup: invalid data-pids JSON', e);
        this.pids = [];
      }

      this.productImageName = parent.getAttribute('data-product-swatch-name') || '';
      this.showCrosssellPercentage = parent.getAttribute('data-show-crosssell-percentage') || 'false';
      this.showSalePercentage = this.showCrosssellPercentage === 'true';
      this.hideSwatches = parent.getAttribute('data-hide-swatches') !== 'false';
      this.hideQuickview = parent.getAttribute('data-hide-quickview') === 'true';
      this.disableSlider = parent.getAttribute('data-disable-slider') === 'true';
      const shimmerGloballyEnabled = window.siteGlobals?.loading_shimmer_enable !== false;
      this.hideSkeleton = parent.getAttribute('data-hide-skeleton') === 'true' || !shimmerGloballyEnabled;
      this.gridLarge = parseInt(parent.getAttribute('data-grid-large')) || 0;
      this.gridSmall = parseInt(parent.getAttribute('data-grid-small')) || 0;
      this.asCarouselSlides = parent.getAttribute('data-as-carousel-slides') === 'true';
      this.wrapClass = parent.getAttribute('data-item-wrap-class') || (this.asCarouselSlides ? 'product-grid-slide' : '');
      this.afterMountEvent = parent.getAttribute('data-after-mount-event') || '';
      this.collectionId = parent.getAttribute('data-collection-id') || '';
      this.collectionHandle = parent.getAttribute('data-collection-handle') || '';
      this.fsWidgetId = parent.getAttribute('data-fs-widget-id') || '';

      const globals = window.siteGlobals || {};
      this.productCategoryMetafieldOneAttr = globals.product_cat_metafield_one || '';
      this.productCategoryMetafieldTwoAttr = globals.product_cat_metafield_two || '';
      this.productCategoryMetafieldOneCollectionAttr = globals.product_cat_metafield_one_collection || '';
      this.productCategoryMetafieldTwoCollectionAttr = globals.product_cat_metafield_two_collection || '';
      this.productReminderMetafieldEmbedAttr = globals.set_reminder_metafield || '';
      this.setReminderLinkText = globals.set_reminder_link_text || '';

      if (this.pids.length === 0) {
        this.finalize(0);
        return;
      }

      this.fetchProducts();
    },
    unmounted() {
      if (this.handleViewportSize) this.handleViewportSize();
    },
    methods: {
      fetchProducts() {
        whenFastSimonReady(
          () => {
            if (!window.FastSimonSDK || typeof window.FastSimonSDK.getProducts !== 'function') {
              logFastSimonError('ProductTileGroup: FastSimonSDK.getProducts unavailable');
              this.loadFailed = true;
              this.finalize(0);
              return;
            }
            try {
              window.FastSimonSDK.getProducts({
                productIds: this.pids,
                direct: true,
                withAttributes: true,
                withProductVariants: true,
                withSiblings: true,
                callback: products => this.handleProductsResponse(products)
              });
            } catch (err) {
              logFastSimonError('ProductTileGroup: getProducts threw', err);
              this.loadFailed = true;
              this.finalize(0);
            }
          },
          {
            onTimeout: () => {
              this.loadFailed = true;
              this.finalize(0);
            }
          }
        );
      },
      handleProductsResponse(products) {
        const list = Array.isArray(products) ? products : [];
        const byId = new Map();
        list.forEach(p => {
          if (p && p.id != null) byId.set(String(p.id), p);
        });
        this.productsOnDisplay = this.pids.map(pid => byId.get(String(pid))).filter(p => p && p.l);
        this.finalize(this.productsOnDisplay.length);
      },
      finalize(count) {
        this.hasLoaded = true;
        emitter.emit('product-tile-group:loaded', { count });
        const parent = this.$el?.parentElement;
        if (parent) {
          parent.dispatchEvent(new CustomEvent('product-tile-group:loaded', {
            bubbles: true,
            detail: { count }
          }));
        }
        if (count === 0 && this.pids.length > 0) {
          const wrapperId = parent?.getAttribute('id') || '(unknown)';
          console.warn(`[ProductTileGroup] FS returned 0 products for ${this.pids.length} pids on #${wrapperId}`);
        }
        if (this.afterMountEvent && count > 0) {
          this.$nextTick(() => {
            const p = this.$el?.parentElement;
            if (p) p.dispatchEvent(new CustomEvent(this.afterMountEvent, { bubbles: true }));
          });
        }
      },
      metaOne(index) {
        return getProductMetafieldFromFS(this.productsOnDisplay, index, this.productCategoryMetafieldOneAttr);
      },
      metaTwo(index) {
        return getProductMetafieldFromFS(this.productsOnDisplay, index, this.productCategoryMetafieldTwoAttr);
      },
      metaOneCollection(index) {
        return getProductCollectionMetafieldFromFS(this.productsOnDisplay, index, this.productCategoryMetafieldOneCollectionAttr);
      },
      metaTwoCollection(index) {
        return getProductCollectionMetafieldFromFS(this.productsOnDisplay, index, this.productCategoryMetafieldTwoCollectionAttr);
      },
      reminderEmbed(index) {
        return getProductMetafieldFromFS(this.productsOnDisplay, index, this.productReminderMetafieldEmbedAttr);
      }
    }
  };
</script>

<template>
  <template v-if="shouldRender">
    <template v-for="(product, index) in productsOnDisplay" :key="product.id">
      <div v-if="wrapClass" :class="wrapClass" data-item :data-carousel-slide="asCarouselSlides ? '' : null">
        <ProductListItem :fsData="product" :useFsProductData="'true'" :pid="String(product.id)" :productTitle="product.l" :productImageName="productImageName" :slideIndex="index" :productIndex="index" :hideSwatches="hideSwatches" :hideQuickview="hideQuickview" :disableSlider="disableSlider" :gridLarge="gridLarge || undefined" :gridSmall="gridSmall || undefined" :collectionId="collectionId" :collectionHandle="collectionHandle" :fsWidgetId="fsWidgetId" :productCategoryMetafieldOne="metaOne(index)" :productCategoryMetafieldTwo="metaTwo(index)" :productCategoryMetafieldOneCollection="metaOneCollection(index)" :productCategoryMetafieldTwoCollection="metaTwoCollection(index)" :productReminderMetafieldEmbed="reminderEmbed(index)" :setReminderLinkText="setReminderLinkText" :showSalePercentage="showSalePercentage" />
      </div>
      <ProductListItem v-else :fsData="product" :useFsProductData="'true'" :pid="String(product.id)" :productTitle="product.l" :productImageName="productImageName" :slideIndex="index" :productIndex="index" :hideSwatches="hideSwatches" :hideQuickview="hideQuickview" :disableSlider="disableSlider" :gridLarge="gridLarge || undefined" :gridSmall="gridSmall || undefined" :collectionId="collectionId" :collectionHandle="collectionHandle" :fsWidgetId="fsWidgetId" :productCategoryMetafieldOne="metaOne(index)" :productCategoryMetafieldTwo="metaTwo(index)" :productCategoryMetafieldOneCollection="metaOneCollection(index)" :productCategoryMetafieldTwoCollection="metaTwoCollection(index)" :productReminderMetafieldEmbed="reminderEmbed(index)" :setReminderLinkText="setReminderLinkText" :showSalePercentage="showSalePercentage" />
    </template>
  </template>
  <template v-else-if="!hasLoaded && !hideSkeleton && pids.length > 0">
    <template v-for="(pid, index) in pids" :key="'ptg-skel-' + pid + '-' + index">
      <div v-if="wrapClass" :class="wrapClass" data-item :data-carousel-slide="asCarouselSlides ? '' : null">
        <div class="product-grid-item loading-placeholder">
          <div class="product-image-placeholder"></div>
          <div class="product-title-placeholder"></div>
          <div class="product-price-placeholder"></div>
          <div class="product-metafield-placeholder"></div>
        </div>
      </div>
      <div v-else class="product-grid-item loading-placeholder">
        <div class="product-image-placeholder"></div>
        <div class="product-title-placeholder"></div>
        <div class="product-price-placeholder"></div>
        <div class="product-metafield-placeholder"></div>
      </div>
    </template>
  </template>
</template>

<style lang="scss">
  .product-tile-group {
    .loading-placeholder {
      width: 100%;

      .product-image-placeholder,
      .product-title-placeholder,
      .product-price-placeholder,
      .product-metafield-placeholder {
        position: relative;
        background-color: var(--hairline, #f6f6f6);
        overflow: hidden;

        &::before {
          content: '';
          position: absolute;
          inset: -30%;
          width: 200%;
          height: 200%;
          background: linear-gradient(to right, transparent 16%, var(--shimmerColor, var(--bg)) 32%, transparent 48%);
          animation: placeholderShimmer 2.4s linear infinite;
          transform: translateX(-100%) rotate(32deg);
          transform-origin: center;
          pointer-events: none;
          z-index: 1;
        }
      }

      .product-image-placeholder {
        width: 100%;
        aspect-ratio: var(--PRODUCT-GRID-ASPECT-RATIO-DECIMAL);
        margin-bottom: 1rem;
      }

      .product-title-placeholder {
        height: 20px;
        width: 80%;
        margin-bottom: 0.5rem;
      }

      .product-price-placeholder {
        height: 14px;
        width: 40%;
      }

      .product-metafield-placeholder {
        height: 14px;
        width: 60%;
        margin-top: 0.5rem;
      }
    }
  }
</style>
